import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Dropdown, Spin } from "antd";
import { FiDownload } from "react-icons/fi";
import AreaChart from "../../../components/global/charts/AreaChart.jsx";
import BarChart from "../../../components/global/charts/BarChart.jsx";
import StatisticsCard from "../dashboard/components/StatisticsCard.jsx";
import {
  GetVolumeReportSummaryApi,
  GetVolumeReportTopSymbolsApi,
  GetVolumeReportTrendApi,
} from "../../../api/request/salesDashboard/reports.js";
import {
  GetAllOffices,
  GetAllTeamLeaders,
} from "../../../api/request/subadmin.js";
import {
  GetSalesAgentsApi,
  buildSalesAgentsParams,
  mapSalesAgentsToOptions,
} from "../../../api/request/salesDashboard/dashboard.js";
import DashboardFilters from "../dashboard/components/DashboardFilters.jsx";
import { exportAsCSV } from "../../../services/exportAsCSV";
import {
  formatNumber,
  getChangeClass,
} from "../../../columns/salesDashboard/reports/volumeReportFormatters.js";
import { getVolumeReportMetricCards } from "../../../columns/salesDashboard/reports/volumeReportColumns.jsx";
import { getResponseData } from "../../../utils/apiHelpers.js";
import { useUserStore } from "../../../store/userStore.js";

const buildQuery = (params) =>
  new URLSearchParams(
    Object.fromEntries(
      Object.entries(params).filter(
        ([, value]) => value !== undefined && value !== null && value !== "",
      ),
    ),
  ).toString();

const VolumeReport = () => {
  const [isLoading, setIsLoading] = useState(false);
  const userDetails = useUserStore((state) => state.userDetails);
  const isSales = userDetails?.department === "Sales";
  const isAgent = isSales && !userDetails?.isTeamLeader;
  const isTeamLeader = isSales && userDetails?.isTeamLeader;
  const teamLeadId = isSales ? userDetails?.userId : undefined;
  const [offices, setOffices] = useState([]);
  const [teams, setTeams] = useState([]);
  const [agents, setAgents] = useState([]);
  const [filters, setFilters] = useState({
    officeId: undefined,
    teamLeadId: teamLeadId,
    agentId: undefined,
    from: undefined,
    to: undefined,
  });
  const [summary, setSummary] = useState({});
  const [trend, setTrend] = useState([]);
  const [topSymbols, setTopSymbols] = useState([]);
  const [isExportLoading, setIsExportLoading] = useState(false);

  const fetchFilterOptions = async () => {
    try {
      const data = getResponseData(await GetAllOffices());
      if (data?.success) {
        setOffices(
          (data?.data || []).map((item) => ({
            label: item?.officeName || item?.name,
            value: item?.id || item?.officeId,
          })),
        );
      }
    } catch (error) {
      setOffices([]);
    }
  };

  const fetchTeams = async () => {
    if (!filters.officeId) {
      setTeams([]);
      return;
    }
    try {
      const data = getResponseData(await GetAllTeamLeaders(filters.officeId));
      if (data?.success) {
        setTeams(
          (data?.data || []).map((item) => ({
            label: item?.fullName || item?.teamLeadName,
            value: item?.userId || item?.teamLeadId,
          })),
        );
      }
    } catch (error) {
      setTeams([]);
    }
  };

  const fetchAgents = async () => {
    if (!filters.teamLeadId) {
      setAgents([]);
      return;
    }
    try {
      const data = getResponseData(
        await GetSalesAgentsApi(buildSalesAgentsParams(filters)),
      );
      if (data?.success) {
        setAgents(mapSalesAgentsToOptions(data?.data));
      }
    } catch (error) {
      setAgents([]);
    }
  };

  const fetchReport = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = buildQuery(filters);
      const [summaryRes, trendRes, topSymbolsRes] = await Promise.allSettled([
        GetVolumeReportSummaryApi(params),
        GetVolumeReportTrendApi(params),
        GetVolumeReportTopSymbolsApi(buildQuery({ ...filters })),
      ]);

      if (
        summaryRes.status === "fulfilled" &&
        summaryRes.value?.data?.success
      ) {
        setSummary(summaryRes.value?.data?.data || {});
      } else {
        setSummary({});
      }

      if (trendRes.status === "fulfilled" && trendRes.value?.data?.success) {
        setTrend(trendRes.value?.data?.data || []);
      } else {
        setTrend([]);
      }

      if (
        topSymbolsRes.status === "fulfilled" &&
        topSymbolsRes.value?.data?.success
      ) {
        setTopSymbols(topSymbolsRes.value?.data?.data || []);
      } else {
        setTopSymbols([]);
      }
    } catch (error) {
      setSummary({});
      setTrend([]);
      setTopSymbols([]);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchTeams();
  }, [filters.officeId]);

  useEffect(() => {
    fetchAgents();
  }, [filters.teamLeadId, filters.agentId]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleCsvExport = async () => {
    setIsExportLoading(true);
    try {
      const params = buildQuery(filters);
      const data = getResponseData(await GetVolumeReportSummaryApi(params));
      const summaryData = data?.data || {};

      const exportRows = [
        {
          metric: "Total Lots",
          value: formatNumber(summaryData?.totalLots),
        },
        {
          metric: "Closed PNL",
          value: `$${formatNumber(summaryData?.closedPnL, 2)}`,
        },
        {
          metric: "Trading Accounts",
          value: formatNumber(summaryData?.tradingAccounts),
        },
        {
          metric: "Avg Volume / Client",
          value: `${formatNumber(summaryData?.avgLotsPerTradingAccount, 2)} lots`,
        },
      ];

      if (exportRows.length) {
        exportAsCSV(
          exportRows,
          [],
          `volume-report-summary-${new Date().toISOString().slice(0, 10)}.csv`,
        );
      }
    } finally {
      setIsExportLoading(false);
    }
  };

  const exportMenu = {
    items: [{ key: "csv", label: "Export CSV" }],
    onClick: ({ key }) => {
      if (key === "csv") {
        handleCsvExport();
      }
    },
  };

  const trendChart = useMemo(
    () => ({
      categories: trend.map((item) => item?.label || item?.date || "--"),
      series: [
        {
          name: "Volume Lots",
          data: trend.map((item) =>
            Number(item?.volumeLots || item?.value || 0),
          ),
        },
      ],
    }),
    [trend],
  );

  const symbolChart = useMemo(
    () => ({
      categories: topSymbols.map((item) => item?.symbol || item?.name || "--"),
      series: [
        {
          name: "Volume Lots",
          data: topSymbols.map((item) =>
            Number(item?.volumeLots || item?.lots || item?.value || 0),
          ),
        },
      ],
    }),
    [topSymbols],
  );

  const cards = useMemo(
    () => getVolumeReportMetricCards({ summary, formatNumber }),
    [summary],
  );

  return (
    <div className="space-y-5">
      <DashboardFilters
        isSales={isSales}
        isTeamLeader={isTeamLeader}
        isAgent={isAgent}
        filters={filters}
        setFilters={setFilters}
        offices={offices}
        teamLeaders={teams}
        agents={agents}
        rightActions={
          <Dropdown
            menu={exportMenu}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button
              icon={<FiDownload />}
              type="primary"
              loading={isExportLoading}
            >
              Export
            </Button>
          </Dropdown>
        }
      />

      {isLoading ? (
        <div className="flex justify-center flex-col my-20">
          <Spin size="large" />
          <p className="primary text-center mt-2">Loading...</p>
        </div>
      ) : (
        <>
          <div className="border-2 border-light rounded-[10px] p-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {cards.map((card) => (
                <div key={card.title}>
                  <StatisticsCard
                    title={card.title}
                    value={card.value}
                    icon={card.icon}
                    bgColor={card.bgColor}
                  />
                  {card.delta !== undefined && card.delta !== null && (
                    <span
                      className={`inline-flex mt-2 ml-[57px] px-2 py-0.5 rounded-full text-xs font-semibold ${getChangeClass(card.delta)}`}
                    >
                      {Number(card.delta) >= 0 ? "\u2191" : "\u2193"}{" "}
                      {formatNumber(Math.abs(card.delta), 2)}%
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
            <div className="border-2 border-light rounded-[10px] p-5 overflow-hidden">
              <h2 className="text-title font-semibold mb-4">
                Volume Trend
              </h2>
              <AreaChart data={trendChart} colors={["#14b8a6"]} height={300} />
            </div>
            <div className="border-2 border-light rounded-[10px] p-5 overflow-hidden">
              <h2 className="text-title font-semibold mb-4">
                Top Traded Symbols
              </h2>
              <BarChart
                data={symbolChart}
                colors={["#1d4ed8"]}
                horizontal
                height={300}
                barHeight="62%"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default VolumeReport;
